import React from 'react';
import { DeleteOutlined, WarningOutlined, MailOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import PublicPageLayout from './PublicPageLayout';

const DeleteAccountPage = () => (
  <PublicPageLayout title="Delete Account" subtitle="How to permanently delete your GuffPuff account">
    <div className="space-y-8 text-gray-700 leading-relaxed" style={{ fontSize: '15px', lineHeight: '1.8' }}>
      <section className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
        <div className="flex items-start gap-3">
          <WarningOutlined className="text-yellow-600 text-xl mt-1" />
          <div>
            <h3 className="font-semibold text-yellow-800 mb-1">Important</h3>
            <p className="text-yellow-700">
              Deleting your account is permanent. Your profile, messages, photos, and all associated
              data will be removed and cannot be recovered.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-bold text-gray-800 mb-3">Delete Your Account</h2>
        <p>
          GuffPuff allows you to request deletion of your account and personal data at any time.
          You can delete your account using one of the methods below.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-bold text-gray-800 mb-3">Option 1: Delete from the App</h2>
        <ol className="list-decimal pl-6 space-y-2 text-gray-600">
          <li>Open the GuffPuff app and sign in to your account.</li>
          <li>Go to <strong>Settings</strong>.</li>
          <li>Tap <strong>Account</strong> → <strong>Delete Account</strong>.</li>
          <li>Confirm your password and tap <strong>Delete Permanently</strong>.</li>
        </ol>
      </section>

      <section className="bg-red-50 rounded-xl p-6 border border-red-100">
        <div className="flex items-start gap-3">
          <DeleteOutlined className="text-red-500 text-xl mt-1" />
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">Option 2: Request via Email</h3>
            <p className="text-gray-600 mb-3">
              If you cannot access the app, email us from your registered email address with the
              subject line <strong>"Delete My Account"</strong> and include your username.
            </p>
            <a href="mailto:support@guffpuff.com?subject=Delete%20My%20Account" className="text-cyan-600 font-semibold hover:underline">
              support@guffpuff.com
            </a>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-bold text-gray-800 mb-3">What Gets Deleted</h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-600">
          <li>Your profile, username, and photos</li>
          <li>Messages and conversation history</li>
          <li>Favorites, blocks, and connections</li>
          <li>Subscription and account settings</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-bold text-gray-800 mb-3">Data Retention</h2>
        <p className="text-gray-600">
          After you request deletion, your personal data will be removed within{' '}
          <strong>30 days</strong>. Some information may be retained longer if required by law
          (e.g. fraud prevention or legal obligations). See our{' '}
          <Link to="/privacy-policy" className="text-cyan-600 hover:underline">
            Privacy Policy
          </Link>{' '}
          for details.
        </p>
      </section>

      <section className="bg-gray-50 rounded-xl p-6 border border-gray-100">
        <div className="flex items-start gap-3">
          <MailOutlined className="text-cyan-600 text-xl mt-1" />
          <div>
            <h3 className="font-semibold text-gray-800 mb-1">Need Help?</h3>
            <p className="text-gray-600">
              Contact our support team at{' '}
              <a href="mailto:support@guffpuff.com" className="text-cyan-600 hover:underline">
                support@guffpuff.com
              </a>{' '}
              before deleting if you have questions.
            </p>
          </div>
        </div>
      </section>
    </div>
  </PublicPageLayout>
);

export default DeleteAccountPage;
